import React, { createContext, useContext, useState, useEffect } from 'react';

type Theme = 'green' | 'red';

export interface CardInfo {
  model_id: string;
  display_name: string;
  theme: Theme;
  highlight: string;
  is_default: boolean;
}

interface ThemeContextType {
  theme: Theme;
  gpuCard: string;
  setGpuCard: (card: string) => void;
  cards: CardInfo[];
  cardsLoading: boolean;
  currentCard: CardInfo | undefined;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Apply theme CSS variables immediately (before React renders)
const applyThemeVariables = (theme: Theme) => {
  const root = document.documentElement;

  if (theme === 'green') {
    // Green theme
    root.style.setProperty('--theme-primary', '#52c376');
    root.style.setProperty('--theme-primary-dark', '#3bba61');
    root.style.setProperty('--theme-primary-darker', '#2eb856');
    root.style.setProperty('--theme-primary-light', '#78d196');

    root.style.setProperty('--theme-bg-main', '#000400');
    root.style.setProperty('--theme-bg-modal', '#011001');
    root.style.setProperty('--theme-bg-card', 'rgba(0, 4, 0, 0.95)');
    root.style.setProperty('--theme-bg-card-hover', 'rgba(2, 12, 2, 0.95)');

    root.style.setProperty('--theme-overlay-20', 'rgba(82, 195, 118, 0.20)');
    root.style.setProperty('--theme-overlay-15', 'rgba(59, 186, 97, 0.15)');
    root.style.setProperty('--theme-overlay-14', 'rgba(82, 195, 118, 0.18)');
    root.style.setProperty('--theme-overlay-12', 'rgba(46, 184, 86, 0.15)');
    root.style.setProperty('--theme-overlay-10', 'rgba(59, 186, 97, 0.12)');
    root.style.setProperty('--theme-overlay-08', 'rgba(59, 186, 97, 0.10)');
    root.style.setProperty('--theme-overlay-06', 'rgba(82, 195, 118, 0.08)');

    root.style.setProperty('--theme-border', 'rgba(82, 195, 118, 0.15)');
    root.style.setProperty('--theme-border-light', 'rgba(82, 195, 118, 0.12)');
    root.style.setProperty('--theme-border-subtle', 'rgba(82, 195, 118, 0.06)');

    root.style.setProperty('--theme-shadow-hover', 'rgba(82, 195, 118, 0.03)');
    root.style.setProperty('--theme-glow', 'rgba(82, 195, 118, 0.3)');
  } else {
    // Red theme
    root.style.setProperty('--theme-primary', '#ff4040');
    root.style.setProperty('--theme-primary-dark', '#e60000');
    root.style.setProperty('--theme-primary-darker', '#cc0000');
    root.style.setProperty('--theme-primary-light', '#ff6b6b');

    root.style.setProperty('--theme-bg-main', '#080000');
    root.style.setProperty('--theme-bg-modal', '#180202');
    root.style.setProperty('--theme-bg-card', 'rgba(8, 0, 0, 0.95)');
    root.style.setProperty('--theme-bg-card-hover', 'rgba(20, 4, 4, 0.95)');

    root.style.setProperty('--theme-overlay-20', 'rgba(255, 64, 64, 0.20)');
    root.style.setProperty('--theme-overlay-15', 'rgba(230, 0, 0, 0.15)');
    root.style.setProperty('--theme-overlay-14', 'rgba(255, 64, 64, 0.14)');
    root.style.setProperty('--theme-overlay-12', 'rgba(204, 0, 0, 0.12)');
    root.style.setProperty('--theme-overlay-10', 'rgba(230, 0, 0, 0.10)');
    root.style.setProperty('--theme-overlay-08', 'rgba(230, 0, 0, 0.08)');
    root.style.setProperty('--theme-overlay-06', 'rgba(255, 64, 64, 0.06)');

    root.style.setProperty('--theme-border', 'rgba(255, 64, 64, 0.15)');
    root.style.setProperty('--theme-border-light', 'rgba(255, 64, 64, 0.12)');
    root.style.setProperty('--theme-border-subtle', 'rgba(255, 64, 64, 0.06)');

    root.style.setProperty('--theme-shadow-hover', 'rgba(255, 64, 64, 0.03)');
    root.style.setProperty('--theme-glow', 'rgba(255, 64, 64, 0.3)');
  }
};

// Apply green theme by default on module load (will be updated once cards are fetched)
applyThemeVariables('green');

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cards, setCards] = useState<CardInfo[]>([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [gpuCard, setGpuCardState] = useState<string>(() => {
    return localStorage.getItem('gpuCard') || '';
  });
  const [theme, setThemeState] = useState<Theme>('green');

  // Fetch cards from API on mount
  useEffect(() => {
    const fetchCards = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${apiUrl}/api/cards`);
        if (response.ok) {
          const data: CardInfo[] = await response.json();
          setCards(data);

          // Determine which card to use
          const savedCard = localStorage.getItem('gpuCard');
          const validSavedCard = data.find(c => c.model_id === savedCard);
          const defaultCard = data.find(c => c.is_default) || data[0];

          const cardToUse = validSavedCard || defaultCard;
          if (cardToUse) {
            setGpuCardState(cardToUse.model_id);
            setThemeState(cardToUse.theme);
            applyThemeVariables(cardToUse.theme);
          }
        }
      } catch (error) {
        console.error('Failed to fetch cards:', error);
      } finally {
        setCardsLoading(false);
      }
    };

    fetchCards();
  }, []);

  // Apply theme whenever gpuCard changes
  useEffect(() => {
    const card = cards.find(c => c.model_id === gpuCard);
    if (card) {
      setThemeState(card.theme);
      applyThemeVariables(card.theme);
      localStorage.setItem('gpuCard', gpuCard);
    }
  }, [gpuCard, cards]);

  const setGpuCard = (card: string) => {
    setGpuCardState(card);
  };

  const currentCard = cards.find(c => c.model_id === gpuCard);

  return (
    <ThemeContext.Provider value={{ theme, gpuCard, setGpuCard, cards, cardsLoading, currentCard }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
